/**
 * Стол до начала розыгрыша: в центре внимания — участники за столом и кнопка участия.
 * Сверху — состояние сбора (сколько подтвердили, обратный отсчёт), затем места за столом
 * карточками (свободные — пунктиром) и краткое описание розыгрыша, которое можно раскрыть.
 */
import type { RoomView } from '@mt/protocol';
import { t, tm, tp } from '../i18n';
import { cr, secondsLeft } from '../lib/format';
import { client, useApp } from '../net/client';
import { Avatar, pct, signedCr, toneOf } from './kit';

export function WaitingRoom({ room, now }: { room: RoomView; now: number }) {
  const balance = useApp((s) => s.profile?.balance ?? 0);
  const left = secondsLeft(room.nextStartAt, now);
  const need = Math.max(0, room.minPlayers - room.ready);
  const empty = Math.max(0, room.maxPlayers - room.seats.length);
  const enough = balance >= room.ante;
  const share = Math.min(1, room.ready / room.minPlayers);
  const title =
    room.status === 'countdown'
      ? t('lobby.wr.startIn', { n: left ?? 0 })
      : room.status === 'forming'
        ? need > 0
          ? tp('lobby.wr.waitMore', need)
          : t('lobby.wr.allReady')
        : t('lobby.wr.preparing');
  const summary = t('lobby.wr.summary', { ante: room.ante, min: room.minPlayers, max: room.maxPlayers, seated: room.seats.length, ready: room.ready });
  const notice = tm(room.noticeMsg, room.notice ?? '');

  return (
    <div className="waiting-room">
      <section className="panel wr-hero">
        <div className="wr-status">
          <div className={`wr-ring ${room.status}`} style={{ ['--p' as string]: room.status === 'countdown' ? 1 : share }} aria-hidden="true">
            <span className="mono">{room.status === 'countdown' ? left : `${room.ready}/${room.minPlayers}`}</span>
          </div>
          <div>
            <h2>{title}</h2>
            <p className="muted-line">{room.status === 'forming' && need > 0 ? `${summary}. ${t('lobby.wr.botsNote')}` : `${summary}.`}</p>
          </div>
        </div>
        <div className="wr-cta">
          {!room.meSeated ? (
            <p className="muted-line">{t('lobby.wr.spectating')}</p>
          ) : room.meReady ? (
            <>
              <p className="wr-confirmed">
                <b>{t('lobby.wr.confirmed')}</b> {t('lobby.wr.anteOnStart', { ante: room.ante })}
              </p>
              <button className="btn ghost" onClick={() => client.ready(false)}>
                {t('lobby.wr.cancel')}
              </button>
            </>
          ) : (
            <>
              <button className="btn primary xl" disabled={!enough} onClick={() => client.ready(true)}>
                {t('lobby.wr.join', { ante: room.ante })}
              </button>
              <p className="muted-line small">{enough ? t('lobby.wr.yourBalance', { amount: balance }) : t('lobby.notEnough')}</p>
            </>
          )}
        </div>
      </section>

      {notice && <p className="notice warn">{notice}</p>}

      <section className="seat-grid" aria-label={t('lobby.wr.seats')}>
        {room.seats.map((s) => (
          <article key={s.playerId} className={`seat-card ${s.ready ? 'ready' : ''} ${s.isMe ? 'me' : ''} ${s.connected ? '' : 'offline'}`}>
            <Avatar id={s.playerId} name={s.name} size={44} bot={s.isBot} />
            <div className="sc-body">
              <b className="sc-name">
                {s.name}
                {s.isMe && <em className="tag me">{t('lobby.wr.you')}</em>}
                {s.isBot && <em className="tag">{t('common.bot')}</em>}
              </b>
              <span className="sc-meta mono">{cr(s.balance)}</span>
              {s.stats && (
                <span className="seat-stats" title={t('lobby.wr.gamesCount', { n: s.stats.games })}>
                  <span>
                    {t('lobby.wr.winRateShort')} {pct(s.stats.winRate)}
                  </span>
                  <span className={toneOf(s.stats.net)}>{signedCr(s.stats.net)}</span>
                </span>
              )}
            </div>
            <span className={`sc-state ${s.ready ? 'ok' : ''}`}>{!s.connected ? t('lobby.wr.offline') : s.ready ? t('lobby.wr.ready') : t('lobby.wr.waiting')}</span>
          </article>
        ))}
        {Array.from({ length: empty }, (_, i) => (
          <article key={'e' + i} className="seat-card empty">
            <span className="avatar empty" aria-hidden="true" />
            <span className="dim">{t('lobby.wr.free')}</span>
          </article>
        ))}
      </section>

      <details className="panel wr-rules">
        <summary>{t('lobby.flowTitle')}</summary>
        <ol className="flow">
          <li>
            <b>{t('lobby.flow.prep')}</b>
            <span>{t('lobby.flow.prepTextAnte', { ante: room.ante })}</span>
          </li>
          <li>
            <b>{t('lobby.flow.round1')}</b>
            <span>{t('lobby.flow.round1Text')}</span>
          </li>
          <li>
            <b>{t('lobby.flow.round2')}</b>
            <span>{t('lobby.flow.round2TextWr')}</span>
          </li>
          <li>
            <b>{t('lobby.flow.round3')}</b>
            <span>{t('lobby.flow.round3TextWr')}</span>
          </li>
          <li>
            <b>{t('lobby.flow.target')}</b>
            <span>{t('lobby.flow.targetText')}</span>
          </li>
          <li>
            <b>{t('lobby.flow.settleWr')}</b>
            <span>{t('lobby.flow.settleTextWr')}</span>
          </li>
        </ol>
      </details>
    </div>
  );
}
